import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Satellite, PhoneCall, Volume2, Loader2, Square } from "lucide-react";
import { toast } from "sonner";
import HeroMark from "@/components/HeroMark";

const EASE = [0.16, 1, 0.3, 1];

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1, ease: EASE } },
};

function Line({ children }) {
  return (
    <span className="reveal-mask">
      <motion.span variants={lineChild} className="block">
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const audioRef = useRef(null);
  const [voiceState, setVoiceState] = useState("idle"); // idle | loading | playing

  const toggleVoice = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(`${process.env.REACT_APP_BACKEND_URL}/api/hero-voice`);
      audioRef.current.addEventListener("playing", () => setVoiceState("playing"));
      audioRef.current.addEventListener("ended", () => setVoiceState("idle"));
      audioRef.current.addEventListener("error", () => {
        setVoiceState("idle");
        toast.error("Voice is unavailable right now.");
      });
    }
    if (voiceState === "playing") {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setVoiceState("idle");
      return;
    }
    setVoiceState("loading");
    audioRef.current.play().catch(() => {
      setVoiceState("idle");
      toast.error("Voice is unavailable right now.");
    });
  };

  return (
    <section ref={ref} className="relative h-screen min-h-[720px] w-full overflow-hidden bg-arjuna-ink" data-testid="hero">
      {/* Parallax earth backdrop */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/30596239/pexels-photo-30596239.jpeg"
          alt="Earth from orbit"
          className="h-full w-full object-cover object-center opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-arjuna-ink/70 via-arjuna-ink/40 to-arjuna-ink" />
        <div className="absolute inset-0 grid-lines opacity-40" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10">
        {/* Interactive 3D eagle. large screens */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.6, ease: EASE }}
          className="pointer-events-auto absolute right-8 top-1/2 hidden h-64 w-64 -translate-y-1/2 lg:block xl:h-72 xl:w-72 2xl:h-80 2xl:w-80"
        >
          <HeroMark className="h-full w-full" />
        </motion.div>
        <div className="container-x flex h-full flex-col justify-center pt-28 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mb-8 flex items-center gap-3"
          >
            <span className="flex h-2 w-2 rounded-full bg-arjuna-teal glow-teal" />
            <span className="overline">Earth Observation · India · Live</span>
          </motion.div>

          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            className="font-heading text-5xl font-medium leading-[0.98] tracking-tighter text-white sm:text-6xl lg:text-8xl"
          >
            <Line>Decision intelligence</Line>
            <Line>
              for India&apos;s <span className="text-arjuna-teal">oceans</span>,{" "}
              <span className="text-emerald-800">forests</span>
            </Line>
            <Line>
              and <span className="text-arjuna-agri">agriculture.</span>
            </Line>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            We fuse satellite imagery, radar and AI to monitor 7,500 km of coastline and
            millions of hectares of farmland. surfacing the signals that protect what
            India depends on.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a href="#demo" data-testid="hero-demo-btn" className="btn-primary group">
              Request a Demo
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Link
              to="/sales"
              data-testid="hero-sales-btn"
              className="btn-primary group"
            >
              <PhoneCall className="h-4 w-4" />
              Talk to Sales
            </Link>
            <a href="#livemap" data-testid="hero-explore-btn" className="btn-ghost">
              <Satellite className="h-4 w-4" />
              Explore the live map
            </a>
            <button onClick={toggleVoice} data-testid="hero-voice-btn" className="btn-ghost">
              {voiceState === "loading" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : voiceState === "playing" ? (
                <Square className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
              {voiceState === "playing" ? "Stop" : "Hear it"}
            </button>
          </motion.div>

          {/* telemetry chips */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-16 grid max-w-2xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/10 sm:grid-cols-4"
          >
            {[
              ["7,517 km", "Coastline"],
              ["4.2M ha", "Farmland"],
              ["12.4k", "Vessels / day"],
              ["<30 min", "Revisit"],
            ].map(([v, l]) => (
              <div key={l} className="bg-arjuna-surface/60 p-4">
                <div className="font-mono text-lg font-semibold text-white">{v}</div>
                <div className="mt-1 text-xs text-slate-400">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
